import { useEffect, useRef, useState } from 'react';

export default function CoursesSection() {
  const headingRef = useRef<HTMLHeadingElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
          }
        });
      },
      { threshold: 0.2 }
    );

    if (headingRef.current) {
      observer.observe(headingRef.current);
    }

    return () => {
      if (headingRef.current) {
        observer.unobserve(headingRef.current);
      }
    };
  }, []);

  return (
    <section id="courses" className="py-12 sm:py-16 md:py-20 px-4 sm:px-6 bg-gradient-to-br from-orange-50 to-orange-100">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12 sm:mb-16">
          <h2 
            ref={headingRef}
            className={`text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-orange-700 mb-4 sm:mb-6 transition-all duration-1000 ease-out ${
              isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-12'
            }`}
          >
            コース・料金
          </h2>
          <p className="text-base sm:text-lg text-orange-600 leading-relaxed">
            あなたに合ったコースをお選びください
          </p>
        </div>

        {/* 一般料金 */}
        <div className="mb-8 sm:mb-12">
          <h3 className="text-xl sm:text-2xl font-bold text-orange-700 mb-4 sm:mb-6 text-center">
            一般料金
          </h3>
          <div className="bg-white rounded-xl sm:rounded-2xl shadow-xl overflow-hidden border-2 border-orange-200">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[500px]">
                <thead>
                  <tr className="bg-gradient-to-r from-orange-400 to-orange-500">
                    <th className="py-3 sm:py-4 px-3 sm:px-6 text-left text-white font-bold text-sm sm:text-base md:text-lg">
                      レッスン回数
                    </th>
                    <th className="py-3 sm:py-4 px-3 sm:px-6 text-center text-white font-bold text-sm sm:text-base md:text-lg">
                      月謝(税込)
                    </th>
                    <th className="py-3 sm:py-4 px-3 sm:px-6 text-center text-white font-bold text-sm sm:text-base md:text-lg">
                      1回あたり
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-orange-100">
                    <td className="py-4 sm:py-5 px-3 sm:px-6 text-orange-700 font-medium text-sm sm:text-base md:text-lg">
                      月2回(50分/回)
                    </td>
                    <td className="py-4 sm:py-5 px-3 sm:px-6 text-center text-orange-700 font-bold text-lg sm:text-xl">
                      ¥11,500
                    </td>
                    <td className="py-4 sm:py-5 px-3 sm:px-6 text-center text-orange-600 font-medium text-sm sm:text-base md:text-lg">
                      ¥5,750
                    </td>
                  </tr>
                  <tr className="border-b border-orange-100">
                    <td className="py-4 sm:py-5 px-3 sm:px-6 text-orange-700 font-medium text-sm sm:text-base md:text-lg">
                      月3回(50分/回)
                    </td>
                    <td className="py-4 sm:py-5 px-3 sm:px-6 text-center text-orange-700 font-bold text-lg sm:text-xl">
                      ¥16,200
                    </td>
                    <td className="py-4 sm:py-5 px-3 sm:px-6 text-center text-orange-600 font-medium text-sm sm:text-base md:text-lg">
                      ¥5,400
                    </td>
                  </tr>
                  <tr className="bg-gradient-to-r from-orange-50 to-orange-100">
                    <td className="py-4 sm:py-5 px-3 sm:px-6 text-orange-700 font-medium text-sm sm:text-base md:text-lg relative">
                      月4回(50分/回)
                      <span className="absolute -top-2 -right-2 bg-gradient-to-r from-orange-500 to-orange-600 text-white text-xs font-bold px-2 sm:px-3 py-1 rounded-full shadow-lg">
                        最もお得!
                      </span>
                    </td>
                    <td className="py-4 sm:py-5 px-3 sm:px-6 text-center text-orange-700 font-bold text-lg sm:text-xl">
                      ¥19,800
                    </td>
                    <td className="py-4 sm:py-5 px-3 sm:px-6 text-center text-orange-600 font-bold text-sm sm:text-base md:text-lg">
                      ¥4,950
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* 学割料金 */}
        <div className="mb-8 sm:mb-12 relative">
          <div className="absolute -top-3 sm:-top-4 left-1/2 transform -translate-x-1/2 z-10">
            <span className="bg-gradient-to-r from-orange-500 to-orange-600 text-white text-sm sm:text-base font-bold px-4 sm:px-6 py-1.5 sm:py-2 rounded-full shadow-xl">
              学生限定でお得!!
            </span>
          </div>
          <h3 className="text-xl sm:text-2xl font-bold text-orange-700 mb-4 sm:mb-6 text-center pt-4 sm:pt-6">
            学割料金
          </h3>
          <div className="bg-white rounded-xl sm:rounded-2xl shadow-xl overflow-hidden border-2 border-orange-200">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[600px]">
                <thead>
                  <tr className="bg-gradient-to-r from-orange-400 to-orange-500">
                    <th className="py-3 sm:py-4 px-3 sm:px-6 text-left text-white font-bold text-sm sm:text-base md:text-lg">
                      レッスン回数
                    </th>
                    <th className="py-3 sm:py-4 px-3 sm:px-6 text-center text-white font-bold text-sm sm:text-base md:text-lg">
                      月謝(税込)
                      <div className="text-xs font-normal mt-1 line-through opacity-80">
                        通常価格
                      </div>
                    </th>
                    <th className="py-3 sm:py-4 px-3 sm:px-6 text-center text-white font-bold text-sm sm:text-base md:text-lg">
                      1回あたり
                    </th>
                    <th className="py-3 sm:py-4 px-3 sm:px-6 text-center text-white font-bold text-sm sm:text-base md:text-lg">
                      割引額
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-orange-100">
                    <td className="py-4 sm:py-5 px-3 sm:px-6 text-orange-700 font-medium text-sm sm:text-base md:text-lg">
                      月2回(50分/回)
                    </td>
                    <td className="py-4 sm:py-5 px-3 sm:px-6 text-center">
                      <div className="text-orange-700 font-bold text-lg sm:text-xl">¥9,900</div>
                      <div className="text-orange-400 text-xs sm:text-sm line-through">¥11,500</div>
                    </td>
                    <td className="py-4 sm:py-5 px-3 sm:px-6 text-center text-orange-600 font-medium text-sm sm:text-base md:text-lg">
                      ¥4,950
                    </td>
                    <td className="py-4 sm:py-5 px-3 sm:px-6 text-center text-green-600 font-bold text-sm sm:text-base md:text-lg">
                      -¥1,600
                    </td>
                  </tr>
                  <tr className="border-b border-orange-100">
                    <td className="py-4 sm:py-5 px-3 sm:px-6 text-orange-700 font-medium text-sm sm:text-base md:text-lg">
                      月3回(50分/回)
                    </td>
                    <td className="py-4 sm:py-5 px-3 sm:px-6 text-center">
                      <div className="text-orange-700 font-bold text-lg sm:text-xl">¥13,900</div>
                      <div className="text-orange-400 text-xs sm:text-sm line-through">¥16,200</div>
                    </td>
                    <td className="py-4 sm:py-5 px-3 sm:px-6 text-center text-orange-600 font-medium text-sm sm:text-base md:text-lg">
                      ¥4,633
                    </td>
                    <td className="py-4 sm:py-5 px-3 sm:px-6 text-center text-green-600 font-bold text-sm sm:text-base md:text-lg">
                      -¥2,300
                    </td>
                  </tr>
                  <tr className="bg-gradient-to-r from-orange-50 to-orange-100">
                    <td className="py-4 sm:py-5 px-3 sm:px-6 text-orange-700 font-medium text-sm sm:text-base md:text-lg relative">
                      月4回(50分/回)
                      <span className="absolute -top-2 -right-2 bg-gradient-to-r from-orange-500 to-orange-600 text-white text-xs font-bold px-2 sm:px-3 py-1 rounded-full shadow-lg">
                        最もお得!
                      </span>
                    </td>
                    <td className="py-4 sm:py-5 px-3 sm:px-6 text-center">
                      <div className="text-orange-700 font-bold text-lg sm:text-xl">¥17,100</div>
                      <div className="text-orange-400 text-xs sm:text-sm line-through">¥19,800</div>
                    </td>
                    <td className="py-4 sm:py-5 px-3 sm:px-6 text-center text-orange-600 font-bold text-sm sm:text-base md:text-lg">
                      ¥4,275
                    </td>
                    <td className="py-4 sm:py-5 px-3 sm:px-6 text-center text-green-600 font-bold text-sm sm:text-base md:text-lg">
                      -¥2,700
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          <p className="text-xs sm:text-sm text-orange-600 mt-3 sm:mt-4 text-center px-4">
            ※ 学割適用は学生証の提示が必要です(高校生・大学生・専門学生が対象)
          </p>
        </div>

        {/* 入会金と防音室利用料 */}
        <div className="grid md:grid-cols-2 gap-6 sm:gap-8">
          <div className="bg-white p-6 sm:p-8 rounded-xl sm:rounded-2xl shadow-xl border-2 border-orange-200">
            <div className="flex items-center gap-3 sm:gap-4 mb-3 sm:mb-4">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-orange-400 to-orange-500 rounded-full flex items-center justify-center flex-shrink-0">
                <i className="ri-user-add-line text-white text-xl sm:text-2xl"></i>
              </div>
              <h4 className="text-xl sm:text-2xl font-bold text-orange-700">入会金</h4>
            </div>
            <p className="text-3xl sm:text-4xl font-bold text-orange-600 mb-2">¥5,500</p>
            <p className="text-orange-600 text-sm sm:text-base">
              体験レッスン当日のご入会で無料
            </p>
          </div>

          <div className="bg-white p-6 sm:p-8 rounded-xl sm:rounded-2xl shadow-xl border-2 border-orange-200">
            <div className="flex items-center gap-3 sm:gap-4 mb-3 sm:mb-4">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-orange-400 to-orange-500 rounded-full flex items-center justify-center flex-shrink-0">
                <i className="ri-music-2-line text-white text-xl sm:text-2xl"></i>
              </div>
              <h4 className="text-xl sm:text-2xl font-bold text-orange-700">防音室利用料</h4>
            </div>
            <p className="text-3xl sm:text-4xl font-bold text-orange-600 mb-2">¥0(無料)</p>
            <p className="text-orange-600 text-sm sm:text-base">
              ※月2回コース以上の利用に限ります
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
